/**
 * Optional nutrition preferences layered on top of the diet style.
 *
 * These are preferences, never filters. Discarding candidates would strand the
 * narrow diets: vegan recipes that also clear the low-sodium bar are 4 of 145 in
 * the published catalogue, far short of the 14 distinct meals a lunch+dinner week
 * needs. Scoring instead degrades gracefully — the planner serves those 4 first,
 * then the lowest-sodium remainder, and the board still fills.
 *
 * Thresholds live here because they are product decisions: they are the same
 * numbers that produced the viability counts shared with Ops (2026-07-28), so a
 * drift here silently invalidates that analysis.
 */

export const NUTRITION_PREFERENCE_HIGH_FIBER = 'high_fiber'
export const NUTRITION_PREFERENCE_HEALTHY_FAT = 'healthy_fat'
export const NUTRITION_PREFERENCE_LOW_SODIUM = 'low_sodium'

/** Selectable ids, in the order the wizard renders them. */
export const NUTRITION_PREFERENCE_IDS = Object.freeze([
    NUTRITION_PREFERENCE_HIGH_FIBER,
    NUTRITION_PREFERENCE_HEALTHY_FAT,
    NUTRITION_PREFERENCE_LOW_SODIUM
])

/** Grams of fibre per serving at or above which a recipe counts as high-fibre. */
export const HIGH_FIBER_GRAMS_PER_SERVING = 6

/** Milligrams of sodium per serving at or below which a recipe counts as low-sodium. */
export const LOW_SODIUM_MG_PER_SERVING = 400

/** `fat_type` values whose dominant fat is unsaturated. */
export const HEALTHY_FAT_TYPES = Object.freeze(['olive_oil', 'nuts_seeds', 'avocado', 'oily_fish'])

/**
 * Weight of one satisfied preference.
 *
 * Sibling term `scoreIngredientReuse` returns a count of shared ingredients,
 * realistically 0-5. Keeping this in the same order of magnitude lets a
 * preference outrank a single shared ingredient without flattening reuse
 * altogether, which would make weekly shopping lists more expensive.
 */
export const NUTRITION_PREFERENCE_SCORE_WEIGHT = 1.5

/** Per-preference predicate over the recipe columns the planner projection carries. */
const SATISFIES_PREFERENCE = Object.freeze({
    [NUTRITION_PREFERENCE_HIGH_FIBER]: (recipe) => {
        // Checked explicitly rather than leaning on `null >= 6` coercing to false:
        // if the threshold ever moved to 0, every recipe with no fibre data would
        // silently start satisfying "more fibre".
        const fiber = toNumberOrNull(recipe?.fiber)
        return fiber !== null && fiber >= HIGH_FIBER_GRAMS_PER_SERVING
    },
    [NUTRITION_PREFERENCE_HEALTHY_FAT]: (recipe) => HEALTHY_FAT_TYPES.includes(recipe?.fat_type),
    [NUTRITION_PREFERENCE_LOW_SODIUM]: (recipe) => {
        const sodium = toNumberOrNull(recipe?.sodium_mg)
        return sodium !== null && sodium <= LOW_SODIUM_MG_PER_SERVING
    }
})

/** Postgres numerics arrive as strings over the wire; a missing value must never satisfy a preference. */
function toNumberOrNull(value) {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

/**
 * @param {string} preferenceId
 * @returns {boolean} whether the id is one the planner knows how to score
 */
export function isNutritionPreferenceId(preferenceId) {
    return NUTRITION_PREFERENCE_IDS.includes(preferenceId)
}

/**
 * Keeps only the ids the planner can act on, so an unknown value stored on an
 * old preferences payload cannot reach the scorer.
 *
 * @param {unknown} selected
 * @returns {string[]}
 */
export function normalizeNutritionPreferences(selected) {
    if (!Array.isArray(selected)) return []
    return NUTRITION_PREFERENCE_IDS.filter((preferenceId) => selected.includes(preferenceId))
}

/**
 * @param {object} recipe - carries `fiber`, `fat_type` and `sodium_mg`
 * @param {string[]} selected - preference ids the user turned on
 * @returns {number} 0 when nothing is selected or satisfied; grows with each match
 */
export function scoreNutritionPreferences(recipe, selected) {
    if (!recipe) return 0
    const active = normalizeNutritionPreferences(selected)
    if (active.length === 0) return 0

    const satisfiedCount = active.reduce(
        (count, preferenceId) => count + (SATISFIES_PREFERENCE[preferenceId](recipe) ? 1 : 0),
        0
    )
    return satisfiedCount * NUTRITION_PREFERENCE_SCORE_WEIGHT
}
